import { useEffect, useMemo, useRef, useState } from "react";
import {
  array,
  arrayOf,
  bool,
  func,
  number,
  object,
  oneOfType,
  shape,
  string,
} from "prop-types";

import {
  FS_CDN,
  FS_WATERMARK,
  URL_ASSETS,
  URL_API_SEND_FILES,
} from "../../constants";
import {
  api,
  displayMeter,
  formatNumber,
  getApiErrorMessage,
  scrollIfNeeded,
  validateInput,
} from "../../helpers";

import Alert from "../../styles/components/Alert";
import Button from "../../styles/components/Button";
import Form from "../form";
import Group from "../form/components/Group";
import Label from "../form/components/Label";
import Modal from "../modal";
import ModalFilesPhotos from "./components/ModalFiles.photos";
import ModalFilesReports from "./components/ModalFiles.reports";
import Row from "../form/components/Row";
import Switch from "../form/components/Switch";
import Text from "../form/components/Text";
import TextArea from "../form/components/TextArea";
import WysiwygEditor from "../form/components/WysiwygEditor";

const initData = {
  branding: true,
  note: "",
  recipients: "",
};

const initErrors = {
  note: null,
  recipients: null,
};

const getThumbnail = photos => {
  if (!photos || !photos.length) {
    return null;
  } else {
    const thumb = photos.find(photo => !!photo.liveCropped);
    return !!thumb
      ? `${FS_CDN}/rotate=deg:exif/resize=width:322,height:176,fit:crop/output=format:jpg,compress:true/cache=expiry:max/${thumb.liveCropped}`
      : null;
  }
};

/**
 * Modal form for emailing selected listing files to a customer
 *
 * @param {array}    props.categories
 * @param {function} props.close
 * @param {array}    props.files
 * @param {object}   props.listing
 * @param {bool}     props.open
 * @param {string}   props.targetDevice
 * @param {function} props.trackEvent
 * @param {function} props.trackModalView
 * @param {function} props.updateUser
 * @param {object}   props.user
 * @returns JSX
 * @public
 */
const ModalFiles = ({
  categories,
  close,
  files,
  listing,
  open,
  targetDevice,
  trackEvent,
  trackModalView,
  updateUser,
  user,
}) => {
  const [data, setData] = useState(() => ({
    ...initData,
    email: (!!user && user.email) || "",
    first_name: (!!user && user.first_name) || "",
    last_name: (!!user && user.last_name) || "",
    phone: (!!user && user.phone) || "",
    photos: [],
    reports: [],
  }));
  const [errors, setErrors] = useState(() => ({
    ...initErrors,
    email: null,
    first_name: null,
    last_name: null,
    phone: null,
  }));
  const [inProgress, setInProgress] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [success, setSuccess] = useState(false);

  const header = useRef();
  const form = useRef();
  const isOpen = useRef(false);

  // Stack form columns for mobile
  const shouldStack = useMemo(() => targetDevice === "mobile", [targetDevice]);

  // Get listing thumbnail for email
  const thumbnail = useMemo(() => getThumbnail(listing.photos), [listing]);

  // Group selected files for display
  const { photos, reportsPublic, reportsInternal } = useMemo(() => {
    const grouped = files.reduce(
      (grouped, file) => {
        if (file.fileType === "photo") {
          grouped.photos.push(file);
        } else if (file.fileIsPublic) {
          grouped.reportsPublic.push(file);
        } else {
          grouped.reportsInternal.push(file);
        }

        return grouped;
      },
      { photos: [], reportsPublic: [], reportsInternal: [] }
    );

    return grouped;
  }, [files]);

  /**
   * Handle form error
   * @param {string} message
   * @public
   */
  const handleFormError = message => {
    setInProgress(false);
    setServerError(message);
    scrollIfNeeded(header.current);
  };

  /**
   * Handle API error
   * @param {*} error
   * @public
   */
  const handleApiError = error => {
    handleFormError(getApiErrorMessage(error));
  };

  /**
   * Handle branding switch
   * @public
   */
  const handleBranding = () => {
    setData(prevData => ({
      ...prevData,
      branding: !prevData.branding,
    }));
  };

  /**
   * Handle form input
   * @param {event} event
   * @public
   */
  const handleInput = event => {
    const { target: input } = event;
    const field = input.id;
    const value = !!input ? input.value : null;
    let error = validateInput(input);

    if (field === "recipients") {
      if (
        !!value &&
        !/^[^\s@,]+@[^\s@,]+\.[^\s@,]+(, ?[^\s@,]+@[^\s@,]+\.[^\s@,]+)*$/.test(
          value
        )
      ) {
        error = "Enter a comma-separated list of valid email addresses";
      } else {
        error = error || null;
      }
    }

    if (!!field) {
      setData(prevData => ({ ...prevData, [field]: value }));
      setErrors(prevErrors => ({ ...prevErrors, [field]: error }));
    }
  };

  /**
   * Handle form submission
   * @param {event} event
   * @public
   */
  const handleSubmit = event => {
    event.preventDefault();

    if (!form.current) {
      return false;
    }

    if (!form.current.checkValidity() || !!errors.recipients) {
      [...form.current.elements].forEach(input => {
        if (data.hasOwnProperty(input.id) && input.id !== "branding") {
          handleInput({ target: input });
        }
      });

      handleFormError("Please check form for errors");
      return;
    } else {
      postData();
    }
  };

  /**
   * Post data to API
   * @public
   */
  const postData = () => {
    setInProgress(true);

    const dataToPost = {
      email: data.recipients.replace(/ /g, ""),
      hideBranding: !data.branding,
      files: prepFilesData(files, data.branding, listing?.serial),
      listingFormatted: {
        advertisedPrice: !!listing.advertisedPrice
          ? `$${formatNumber(listing.advertisedPrice)}`
          : null,
        categories: !!categories.length ? categories.join(", ") : null,
        equipmentId: listing.equipmentId,
        location: `${listing.locationCity}, ${listing.locationState} ${listing.locationZip}`,
        make: listing.make,
        meter: displayMeter(listing)
          ? `${formatNumber(listing.meterValue)} ${listing.meterType}`
          : "–",
        model: listing.model,
        serial: listing.serial,
        salePrice: !!listing.salePrice
          ? `$${formatNumber(listing.salePrice)}`
          : null,
        thumbnail: thumbnail,
        year: !!listing.year ? listing.year : "",
      },
      note: data.note,
      salesperson: {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: !!data.phone
          ? data.phone.replace(/.*(\d{3}).*(\d{3}).*(\d{4})(.*)/, "$1-$2-$3$4")
          : null,
      },
      showBranding: data.branding,
    };

    api
      .post(URL_API_SEND_FILES, dataToPost)
      .then(response => {
        if (!!response.data && !!response.data.success) {
          if (!!trackEvent) {
            trackEvent({
              category: "Form",
              action: "Submit",
              label: "Send Files Modal",
            });
          }

          setInProgress(false);
          setServerError(null);
          setSuccess(true);
          updateUser(dataToPost.salesperson);
          scrollIfNeeded(header.current);
        } else {
          handleApiError(response);
        }
      })
      .catch(handleApiError);
  };

  // Respond to modal open/close
  useEffect(() => {
    if (!isOpen.current && open && !!trackModalView) {
      isOpen.current = open;
      trackModalView("sendfiles");
    }

    if (isOpen.current && !open) {
      setData(prevData => ({ ...prevData, ...initData }));
      setErrors(prevErrors => ({ ...prevErrors, ...initErrors }));
      setInProgress(false);
      setServerError(null);
      setSuccess(false);
    }
  }, [open, trackEvent, trackModalView]);

  return (
    <Modal aria-label="Send Files" close={close} open={open}>
      <header ref={header}>
        <h1>
          <strong>Send Files</strong>
        </h1>
        <p>
          A link to download the selected files will be automatically added to
          the email along with your message. We will also send you a copy of the
          message.
        </p>

        {serverError && <Alert type="error">{serverError}</Alert>}
        {success && (
          <Alert type="success">Thanks! Your email is on its way.</Alert>
        )}
      </header>
      {!success && (
        <Form onSubmit={handleSubmit} formRef={form}>
          <div className="form-fields">
            <ModalFilesPhotos photos={photos} targetDevice={targetDevice} />
            <ModalFilesReports
              label="Public-facing Reports"
              reports={reportsPublic}
            />
            <ModalFilesReports
              label="Internal-only Reports"
              reports={reportsInternal}
            />
            <Group error={errors.note}>
              <Label tag="span" id="note">
                Message
              </Label>
              <WysiwygEditor
                ariaLabeledBy="note"
                handleInput={handleInput}
                value={data.note}
              />
            </Group>
            <Group
              error={errors.recipients}
              help="Separate multiple emails with comma"
            >
              <Label htmlFor="recipients" required>
                Recipients
              </Label>
              <TextArea
                height="short"
                id="recipients"
                onChange={handleInput}
                required
                value={data.recipients}
              />
            </Group>
            <Group>
              <Label htmlFor="branding">HOLT Branding</Label>
              <Switch
                checked={data.branding}
                id="branding"
                onChange={handleBranding}
              />
            </Group>
            <Row stack={shouldStack}>
              <Group error={errors.first_name}>
                <Label htmlFor="first_name" required>
                  Your First Name
                </Label>
                <Text
                  id="first_name"
                  maxLength="30"
                  onChange={handleInput}
                  required
                  value={data.first_name}
                />
              </Group>
              <Group error={errors.last_name}>
                <Label htmlFor="last_name" required>
                  Your Last Name
                </Label>
                <Text
                  id="last_name"
                  maxLength="30"
                  onChange={handleInput}
                  required
                  value={data.last_name}
                />
              </Group>
            </Row>
            <Row stack={shouldStack}>
              <Group error={errors.phone}>
                <Label htmlFor="phone" required>
                  Your Phone Number
                </Label>
                <Text
                  id="phone"
                  maxLength="20"
                  onChange={handleInput}
                  required
                  type="tel"
                  value={data.phone}
                />
              </Group>
              <Group>
                <Label htmlFor="email" required>
                  Email
                </Label>
                <Text id="email" readOnly value={data.email} />
              </Group>
            </Row>
          </div>
          <div className="form-actions">
            <Button
              block={targetDevice === "mobile"}
              color="green400"
              disabled={inProgress}
              size="small"
              square={targetDevice === "mobile"}
              type="submit"
            >
              {inProgress ? "Sending…" : "Send"}
            </Button>
          </div>
        </Form>
      )}
    </Modal>
  );
};

/**
 * Prep files data for form submission
 *
 * @param {array} files
 * @param {bool} branding
 * @param {number|string} serial
 * @returns {array}
 */
function prepFilesData(files = [], branding, serial) {
  let photoNumber = 0;
  const filesData = files.map(file => {
    if (file.fileType === "photo") {
      photoNumber++;
      return getPhotoUrl(file, branding, serial, photoNumber);
    } else {
      return `${URL_ASSETS}/${file.fileName.replace(/\s+/g, "%20")}`;
    }
  });

  return filesData;
}

const photoSize = 1280;
const watermarkSize = 30;
const watermarkParam = `watermark=file:${FS_WATERMARK}%2Csize:${watermarkSize}%2Cposition:[bottom%2Cright]`;
const photoFormat = "jpg";
const photoUrl = [
  FS_CDN,
  "rotate=deg:exif",
  `resize=width:${photoSize}%2Cheight:${photoSize}`,
  `output=format:${photoFormat}%2Ccompress:true`,
];

/**
 * Get photo URL for form submission
 *
 * @param {object} file
 * @param {bool} branding
 * @param {string} serial
 * @param {number} photoNumber
 * @returns {string}
 */
function getPhotoUrl(file, branding, serial, photoNumber) {
  const baseUrl = branding ? [...photoUrl, watermarkParam] : photoUrl;
  const photoNumberPadded = `${photoNumber}`.padStart(3, "0");
  const prefix = branding ? "holtused-" : "";
  const downloadName = `content=filename:${prefix}${serial}-${photoNumberPadded}.${photoFormat}`;
  return [...baseUrl, downloadName, file.fileName].join("/");
}

ModalFiles.propTypes = {
  categories: array,
  close: func.isRequired,
  files: arrayOf(
    shape({
      fileDate: oneOfType([object, string]),
      fileId: oneOfType([number, string]),
      fileIndex: number,
      fileIsPublic: bool,
      fileName: string,
      fileTitle: string,
      fileType: string,
    })
  ),
  listing: shape({
    equipmentId: oneOfType([number, string]),
  }),
  open: bool,
  targetDevice: string,
  trackEvent: func,
  trackModalView: func,
  updateUser: func.isRequired,
  user: shape({
    email: string,
    first_name: string,
    last_name: string,
    phone: string,
  }),
};

ModalFiles.defaultProps = {
  categories: [],
  files: [],
  open: false,
};

export default ModalFiles;
